import { ReactNode, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, CornerDownRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { WorkflowVersionSummary } from "@/types/dialer/workflow";
import { formatParis } from "@/utils/dialer-datetime";
import { ScenarioPill, scenarioPillState } from "./scenario-pill";
import { VersionBadge } from "./version-badge";

export type LineageBlock = { kind: "version"; version: WorkflowVersionSummary } | { kind: "drafts"; author: string; versions: WorkflowVersionSummary[] };

/**
 * Immutability means one version per save, so an afternoon of editing produces a dozen
 * of them. Consecutive drafts by the same author are folded into one expandable block,
 * without which the lineage becomes unreadable in half a day.
 */
export function foldLineage(versions: WorkflowVersionSummary[]): LineageBlock[] {
    const blocks: LineageBlock[] = [];
    versions.forEach(version => {
        const author = version.created_by?.name ?? "Unknown";
        const foldable = version.status === "draft" && version.deployed_in.length === 0 && version.cards_active === 0;
        const last = blocks[blocks.length - 1];
        if (foldable && last?.kind === "drafts" && last.author === author) {
            last.versions.push(version);
            return;
        }
        if (foldable && last?.kind === "version" && last.version.status === "draft" && (last.version.created_by?.name ?? "Unknown") === author && last.version.deployed_in.length === 0) {
            blocks[blocks.length - 1] = { kind: "drafts", author, versions: [last.version, version] };
            return;
        }
        blocks.push({ kind: "version", version });
    });
    return blocks;
}

type VersionLineageProps = {
    versions: WorkflowVersionSummary[];
    selectedVersionId?: number | null;
    onSelect: (version: WorkflowVersionSummary) => void;
    /**
     * What the selected version is, unfolded under its own row. Given as a function rather
     * than a node so the frieze owns *where* the detail appears while the caller keeps the
     * gestures and the state they need.
     */
    renderDetail?: (version: WorkflowVersionSummary) => ReactNode;
};

/**
 * One version of the frieze, selectable anywhere on it, and the panel describing it when it
 * is the one being read.
 *
 * The whole row is the button, deliberately: what a row offers is one gesture — « I am
 * looking at this version » — and everything drawn on it is the reason to choose it, not a
 * target of its own. The rule that comes with that pattern is that nothing inside may be
 * interactive, or the row acquires holes that swallow a click; `ScenarioPill` breaks it the
 * moment it becomes a button again. The panel is therefore a sibling of the button and not
 * a child of it, inside a shared frame that draws the two as one block.
 *
 * That frame is the whole point of unfolding here rather than under the frieze. A panel
 * placed after the list is a third card with the weight of a version, at a distance that
 * grows with the number of versions; a panel inside the block whose border encloses both
 * cannot belong to another row. The row states the same thing to a screen reader through
 * `aria-expanded` and `aria-controls`, and the panel names its version in a heading, so the
 * link survives having no border to look at.
 *
 * The pill's sentence is carried here as the row's accessible description, so tabbing to a
 * version still announces why its verdict is amber or red.
 */
function VersionRow({ version, selected, onSelect, detail }: { version: WorkflowVersionSummary; selected: boolean; onSelect: () => void; detail?: ReactNode }) {
    const scenarios = scenarioPillState(version.scenarios);
    const noteId = `lineage-scenarios-${version.id}`;
    const detailId = `lineage-detail-${version.id}`;
    const headingId = `lineage-detail-heading-${version.id}`;
    // Undefined on a frieze rendered without a detail, so those rows claim no disclosure.
    const unfolds = detail !== undefined;
    const open = unfolds && selected;
    return (
        <div className={cn("overflow-hidden rounded-md border bg-white", selected && "border-primary ring-1 ring-primary")}>
            <button
                type="button"
                onClick={onSelect}
                // Absent rather than `false` on the other rows: a list where every item
                // claims to be the current one, even negatively, is a list that says nothing.
                aria-current={selected ? "true" : undefined}
                aria-expanded={unfolds ? open : undefined}
                aria-controls={open ? detailId : undefined}
                aria-describedby={noteId}
                data-testid={`lineage-version-${version.version}`}
                className="w-full p-2 text-left transition-colors hover:bg-gray-50"
            >
                <div className="flex flex-wrap items-center gap-2">
                    <VersionBadge version={version.version} statusBadge={version.status_badge} />
                    <ScenarioPill summary={version.scenarios} />
                    {version.deployed_in.map(target => (
                        <Badge key={`${target.campaign_code}-${target.slot}`} tone="info">
                            {target.slot} @ {target.campaign_code}
                        </Badge>
                    ))}
                    {version.issue_counts?.error > 0 && <Badge tone="danger">{version.issue_counts.error} errors</Badge>}
                    {version.issue_counts?.warning > 0 && <Badge tone="warning">{version.issue_counts.warning} warnings</Badge>}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                    {version.created_by?.name ?? "Unknown"} · {formatParis(version.created_at)}
                    {version.cards_active > 0 ? ` · ${version.cards_active} active cards` : ""}
                </p>
                {version.notes && <p className="mt-1 text-xs text-gray-700">{version.notes}</p>}
            </button>
            {/* Beside the row rather than inside it: within the button this sentence would be
                read once as part of its name and once as its description. */}
            <span id={noteId} className="sr-only">
                {scenarios.tooltip}
            </span>
            {open && (
                <section id={detailId} role="region" aria-labelledby={headingId} data-testid={`lineage-detail-${version.version}`} className="border-t bg-gray-50 p-3">
                    <h3 id={headingId} className="mb-3 flex items-center gap-1.5 text-sm font-normal text-black">
                        <CornerDownRight className="h-4 w-4 shrink-0 text-gray-500" />
                        Details of the selected version, v{version.version}
                    </h3>
                    {detail}
                </section>
            )}
        </div>
    );
}

export function VersionLineage({ versions, selectedVersionId, onSelect, renderDetail }: VersionLineageProps) {
    const blocks = useMemo(() => foldLineage(versions), [versions]);
    // Keyed by the first version of a fold rather than by its position: publishing or
    // discarding reloads the frieze, and a position then names a different block, so the
    // fold the operator had opened would close and its neighbour would open in its place.
    const [expanded, setExpanded] = useState<Record<number, boolean>>({});

    // `undefined` rather than an empty node when the caller asks for no detail: it is what
    // tells a row it has no disclosure to announce.
    const detailOf = (version: WorkflowVersionSummary) => (renderDetail ? renderDetail(version) : undefined);

    return (
        <ol className="space-y-2">
            {blocks.map((block, index) => {
                if (block.kind === "version") {
                    return (
                        <li key={block.version.id}>
                            <VersionRow version={block.version} selected={selectedVersionId === block.version.id} onSelect={() => onSelect(block.version)} detail={detailOf(block.version)} />
                        </li>
                    );
                }
                const foldId = block.versions[0].id;
                const open = expanded[foldId] ?? block.versions.some(version => version.id === selectedVersionId);
                const listId = `lineage-drafts-${foldId}`;
                return (
                    <li key={`drafts-${index}`} className="space-y-2">
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            aria-expanded={open}
                            aria-controls={listId}
                            className="w-full justify-start text-xs"
                            onClick={() => setExpanded(previous => ({ ...previous, [foldId]: !open }))}
                        >
                            {open ? <ChevronDown className="mr-1 h-4 w-4" /> : <ChevronRight className="mr-1 h-4 w-4" />}
                            {block.versions.length} drafts by {block.author}
                        </Button>
                        {open && (
                            <ol id={listId} className="space-y-2 border-l pl-3">
                                {block.versions.map(version => (
                                    <li key={version.id}>
                                        <VersionRow version={version} selected={selectedVersionId === version.id} onSelect={() => onSelect(version)} detail={detailOf(version)} />
                                    </li>
                                ))}
                            </ol>
                        )}
                    </li>
                );
            })}
        </ol>
    );
}
